import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Sparkles, Search, FileText, Link2, BarChart3, Shield, Wand2, CheckCircle2, Github, Twitter, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import AdSenseSlot from "@/components/AdSenseSlot";

const HERO_BG = "https://images.unsplash.com/photo-1762279389083-abf71f22d338?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxkYXRhJTIwYW5hbHl0aWNzJTIwY2hhcnQlMjBhYnN0cmFjdHxlbnwwfHx8fDE3NzgzMTI2MjR8MA&ixlib=rb-4.1.0&q=85";
const SEO_IMG = "https://images.pexels.com/photos/7119258/pexels-photo-7119258.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TEAM_IMG = "https://images.unsplash.com/photo-1758873268663-5a362616b5a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBzdGFydHVwJTIwdGVhbXxlbnwwfHx8fDE3NzgzMTI2MjR8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Search, title: "AI SEO Audit", desc: "Drop a URL. Get a 100-point breakdown with severity-tagged issues, fixes and quick wins.", testid: "feature-audit" },
  { icon: FileText, title: "Blog Generator", desc: "Long-form posts with FAQ, internal links, meta tags and a featured image — ready to publish.", testid: "feature-blog" },
  { icon: BarChart3, title: "Keyword Research", desc: "Search volumes, difficulty, intent, and trends. Surface low-comp gold in seconds.", testid: "feature-keywords" },
  { icon: Link2, title: "Backlink Outreach", desc: "Convertable, personalized outreach emails + a tracker for every link in your portfolio.", testid: "feature-backlinks" },
  { icon: Wand2, title: "Free Tool Generator", desc: "Spin up calculators and checkers with lead capture — drive qualified organic traffic.", testid: "feature-tools" },
  { icon: Shield, title: "Admin Control", desc: "Manage users, monitor AI usage and subscriptions from a clean operational panel.", testid: "feature-admin" },
];

const PLANS = [
  { name: "Starter", price: "29", description: "For freelancers shipping content fast.", features: ["20 SEO audits / mo", "10 blog posts / mo", "Basic keyword research", "Email support"], cta: "Start free trial", highlight: false, testid: "plan-starter" },
  { name: "Pro", price: "79", description: "For growth teams scaling organic.", features: ["100 SEO audits / mo", "Unlimited blog posts", "Advanced keyword research", "Backlink tracking", "Priority support"], cta: "Get Pro", highlight: true, testid: "plan-pro" },
  { name: "Agency", price: "199", description: "For agencies running multiple clients.", features: ["Unlimited audits", "White-label exports", "Multi-workspace", "Free tool generator", "Dedicated success manager"], cta: "Talk to sales", highlight: false, testid: "plan-agency" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [url, setUrl] = useState("");

  const handleAudit = (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    if (user) navigate("/audit", { state: { url } });
    else navigate("/register", { state: { from: "/audit", prefill: url } });
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      {/* nav */}
      <header className="sticky top-0 z-30 border-b border-zinc-800/80 bg-[#09090B]/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-7 h-7 bg-[#0055FF] rounded-sm grid place-items-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-semibold text-lg">RankPilot<span className="text-[#0055FF]"> AI</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features">Features</a>
            <Link to="/about" className="hover:text-white transition-colors" data-testid="nav-about">How it works</Link>
            <a href="#pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
            <Link to="/free-google-ads-audit" className="text-[#10B981] hover:text-[#34D399] transition-colors font-medium" data-testid="nav-free-audit">Free Google Ads Audit</Link>
            <Link to="/dashboard" className="hover:text-white transition-colors" data-testid="nav-dashboard">Dashboard</Link>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Button onClick={() => navigate("/dashboard")} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="nav-cta-app">
                Open app <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" className="text-zinc-300 hover:text-white hover:bg-zinc-900 rounded-sm" data-testid="nav-login">
                  <Link to="/login">Log in</Link>
                </Button>
                <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="nav-signup">
                  <Link to="/register">Start free</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-800/80">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/80 via-[#09090B]/70 to-[#09090B]" />
        </div>
        <div className="grain absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-28 lg:pt-32 lg:pb-36">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-in">
              <Badge variant="outline" className="rounded-sm border-[#0055FF]/40 bg-[#0055FF]/10 text-[#3377FF] px-3 py-1 mb-6" data-testid="hero-eyebrow">
                <Sparkles className="w-3 h-3 mr-1.5" /> POWERED BY GPT-5.2
              </Badge>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]" data-testid="hero-title">
                Rank on Google.<br />
                <span className="text-zinc-500">On autopilot.</span>
              </h1>
              <p className="mt-6 text-lg text-zinc-400 max-w-xl leading-relaxed" data-testid="hero-subtitle">
                RankPilot AI audits your site, generates SEO-optimized content, finds low-competition keywords and writes outreach — so your rankings climb while you sleep.
              </p>

              <form onSubmit={handleAudit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="hero-audit-form">
                <Input
                  type="url"
                  required
                  placeholder="https://yoursite.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="h-14 bg-zinc-950 border-zinc-800 text-base rounded-sm focus-visible:ring-[#0055FF] focus-visible:ring-2 focus-visible:ring-offset-0"
                  data-testid="hero-url-input"
                />
                <Button type="submit" size="lg" className="h-14 px-8 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm font-medium glow-beam" data-testid="hero-audit-cta">
                  Audit my site <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-zinc-500">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> No credit card</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> 30-second setup</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> Cancel anytime</div>
              </div>
            </div>

            <div className="lg:col-span-5 fade-in" style={{ animationDelay: "120ms" }}>
              <div className="relative">
                <div className="absolute -inset-px bg-gradient-to-br from-[#0055FF]/30 via-transparent to-transparent rounded-sm" />
                <div className="relative bg-zinc-950/80 border border-zinc-800 rounded-sm p-6 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-4">
                    <span className="label-eyebrow">Live Audit Preview</span>
                    <span className="text-xs text-zinc-500 font-mono">EXAMPLE.COM</span>
                  </div>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="font-display text-7xl font-semibold tracking-tighter">82</span>
                    <span className="text-zinc-500 text-sm">/100</span>
                    <Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30 rounded-sm ml-auto">GRADE B+</Badge>
                  </div>
                  <div className="space-y-3">
                    {[
                      { label: "Technical SEO", v: 88, c: "#10B981" },
                      { label: "On-Page", v: 76, c: "#0055FF" },
                      { label: "Content Quality", v: 91, c: "#10B981" },
                      { label: "Backlinks", v: 64, c: "#F59E0B" },
                    ].map((r) => (
                      <div key={r.label}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-zinc-400">{r.label}</span>
                          <span className="text-zinc-300 font-mono">{r.v}</span>
                        </div>
                        <div className="h-1 bg-zinc-900 rounded-sm overflow-hidden">
                          <div className="h-full" style={{ width: `${r.v}%`, background: r.c, transition: "width 1s ease" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-6 border-t border-zinc-800 text-xs text-zinc-500 leading-relaxed">
                    <span className="text-[#0055FF]">▲ 12 issues found</span> — including a missing H1, slow LCP, and 4 broken internal links.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b border-zinc-800/80">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl mb-16">
            <span className="label-eyebrow" data-testid="features-eyebrow">The Toolkit</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="features-title">
              Six tools. One operator. Zero busywork.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800/80 border border-zinc-800 rounded-sm overflow-hidden">
            {FEATURES.map((f, i) => (
              <div key={f.title} className="bg-[#09090B] hover:bg-zinc-950 p-8 transition-colors group" data-testid={f.testid}>
                <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 rounded-sm grid place-items-center mb-5 group-hover:border-[#0055FF]/40 transition-colors">
                  <f.icon className="w-5 h-5 text-[#0055FF]" />
                </div>
                <h3 className="font-display text-xl font-medium mb-2">{f.title}</h3>
                <p className="text-zinc-400 leading-relaxed text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE BENTO */}
      <section className="border-b border-zinc-800/80">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-8">
          <div className="md:row-span-2 bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden relative group">
            <img src={SEO_IMG} alt="" className="w-full h-72 md:h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black via-black/80 to-transparent">
              <span className="label-eyebrow">CASE STUDY</span>
              <h3 className="font-display text-2xl font-medium mt-2 mb-2">From #47 → #3 in 90 days.</h3>
              <p className="text-zinc-400 text-sm">A SaaS founder used RankPilot's audit + blog generator to dominate their core keyword.</p>
            </div>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8">
            <span className="label-eyebrow">Real-time</span>
            <h3 className="font-display text-2xl font-medium mt-2 mb-3">Animated dashboards.</h3>
            <p className="text-zinc-400 text-sm leading-relaxed">Live ranking changes, traffic curves, and SEO health scores — visualized clearly.</p>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8 relative overflow-hidden">
            <img src={TEAM_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
            <div className="relative">
              <span className="label-eyebrow">Built for agencies</span>
              <h3 className="font-display text-2xl font-medium mt-2 mb-3">Multi-client by design.</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">White-label exports and workspace separation for serious operators.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-b border-zinc-800/80">
        <div className="max-w-7xl mx-auto px-6 pt-12 pb-4">
          {/* AdSense slot — only shown to anonymous visitors on the public landing page */}
          {!user && (
            <div className="max-w-3xl mx-auto opacity-90" data-testid="adsense-landing-pricing">
              <AdSenseSlot slot="1111111111" />
            </div>
          )}
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="label-eyebrow" data-testid="pricing-eyebrow">Pricing</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="pricing-title">
              Pick a plan. Outrank the rest.
            </h2>
            <p className="mt-4 text-zinc-400">All plans include a 14-day free trial. No credit card required.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map((p) => (
              <div key={p.name} data-testid={p.testid}
                   className={`relative bg-zinc-950 border rounded-sm p-8 transition-transform hover:-translate-y-1 ${p.highlight ? "border-[#0055FF]" : "border-zinc-800"}`}>
                {p.highlight && (
                  <div className="absolute -top-3 left-8 bg-[#0055FF] text-white text-[10px] tracking-[0.2em] px-2 py-1 rounded-sm">MOST POPULAR</div>
                )}
                <h3 className="font-display text-xl font-medium">{p.name}</h3>
                <p className="text-zinc-500 text-sm mt-1">{p.description}</p>
                <div className="mt-6 mb-6 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-semibold tracking-tighter">${p.price}</span>
                  <span className="text-zinc-500 text-sm">/mo</span>
                </div>
                <Button asChild className={`w-full rounded-sm ${p.highlight ? "bg-[#0055FF] hover:bg-[#3377FF]" : "bg-zinc-900 hover:bg-zinc-800 border border-zinc-800"}`} data-testid={`${p.testid}-cta`}>
                  <Link to="/register">{p.cta}</Link>
                </Button>
                <ul className="mt-8 space-y-3">
                  {p.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2 text-sm text-zinc-400">
                      <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-zinc-800/80 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090B] to-[#09090B]/60" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="cta-title">
            Stop guessing. Start ranking.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">Spin up your first AI SEO audit in under a minute.</p>
          <Button asChild size="lg" className="mt-10 h-14 px-10 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="cta-button">
            <Link to="/register">Get started <ArrowRight className="w-5 h-5 ml-2" /></Link>
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#09090B] py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[#0055FF] rounded-sm grid place-items-center">
                <Zap className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="font-display font-medium">RankPilot AI</span>
              <span className="text-zinc-600 text-xs ml-3">© 2026 — All rights reserved</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-zinc-500">
              <Link to="/about" className="hover:text-zinc-300 transition-colors" data-testid="footer-about">How it works</Link>
              <Link to="/privacy" className="hover:text-zinc-300 transition-colors" data-testid="footer-privacy">Privacy</Link>
              <Link to="/terms" className="hover:text-zinc-300 transition-colors" data-testid="footer-terms">Terms</Link>
              <a href="#" className="hover:text-zinc-300 transition-colors" aria-label="Twitter"><Twitter className="w-4 h-4" /></a>
              <a href="#" className="hover:text-zinc-300 transition-colors" aria-label="GitHub"><Github className="w-4 h-4" /></a>
            </div>
          </div>
          <div className="border-t border-zinc-900 pt-4 text-xs text-zinc-600 flex flex-col md:flex-row justify-between gap-2" data-testid="landing-business-info">
            <span><strong className="text-zinc-500">Oz Tech Hub</strong> · 17 Selma Walk, Donnybrook, VIC 3064, Australia</span>
            <span>+61 413 227 784 · <a href="mailto:support@myrankpilotai.com" className="hover:text-zinc-400">support@myrankpilotai.com</a></span>
          </div>
        </div>
      </footer>
    </div>
  );
}
